import styled from 'styled-components';
import { InfoBanner } from 'ui/banner';
import { InputText } from 'ui/inputs';
import { IconButton } from 'ui/button';
import {
  baseColors,
  caption,
  colors,
  fonts,
  fontSizes,
  fontWeights,
  heading4,
  link2,
  spacing,
  textColors,
} from 'ui/theme';
import { pxToRem } from 'utils/convert';

export const HeaderContentContainer = styled.div`
  align-items: center;
  background: none;
  display: flex;
  /** Temporary work-around until angular can be removed (which adds Proxima Nova to the body) */
  font-family: ${fonts.sansSerif};
  [data-test='input-wrapper'] {
    padding: 0;
    :hover {
      border-color: transparent;
    }
    :focus-within {
      box-shadow: none;
      border: none;
    }
  }
`;

export const StyledIconButton = styled(IconButton)`
  height: ${pxToRem(34)};
  :focus::before {
    border: none;
  }
`;

export const StyledInputWrapper = styled.div`
  display: inline-block;
  position: relative;
`;

export const CharacterCounter = styled.div<{ isCompact?: boolean; isInverted?: boolean }>`
  ${({ isCompact }) => (isCompact ? `top: ${pxToRem(1)}` : `bottom: calc(100% + ${pxToRem(4)})`)};
  color: ${({ isInverted }) => (isInverted ? colors.grey15 : colors.textSecondary)};
  font-size: ${({ isCompact }) => (isCompact ? pxToRem(10) : fontSizes.s12)};
  font-weight: ${fontWeights.regular};
  line-height: ${fontSizes.s16};
  position: absolute;
  right: ${({ isCompact }) => (isCompact ? pxToRem(4) : 0)};
`;

export const StyledInput = styled(InputText)<{ isInverted?: boolean }>`
  border: none;
  background-color: inherit;
  display: inline-block;
  margin-left: ${spacing.single}; /** because the outline is implemented with a box-shadow */
  input {
    ${heading4};
    padding: 0;
    background-color: inherit;
    border-bottom: ${spacing.single} dashed ${baseColors.grey15};
    width: 40ch;
    ${({ isInverted }) => (isInverted ? `color: ${colors.white};` : '')}
    :focus {
      border: none;
      border-bottom: ${spacing.single} dashed ${baseColors.grey15};
      background: transparent;
    }
    :active {
      border-bottom: ${spacing.single} dashed ${baseColors.grey15};
    }
  }
`;

// Might need to revisit how this is done for other medium editors
export const TemplateDetailsContainer = styled.ul`
  ${caption};
  align-items: center;
  color: ${textColors.textSecondary};
  display: flex;
  gap: ${pxToRem(11)} ${spacing.half};
  flex-wrap: wrap;
  line-height: 1.15em;
  padding: 0;
`;

export const TemplateNameDisplay = styled.span<{ isInverted?: boolean }>`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  ${heading4};
  line-height: 2.2rem; /** work-around because text descenders are being clipped by overflow hidden */
  border-bottom: ${spacing.single} dashed transparent;
  ${({ isInverted }) => (isInverted ? `color: ${colors.white};` : '')}
`;

export const TemplateDetailsBlock = styled.li`
  display: flex;
  align-items: center;
  min-width: 0;

  white-space: nowrap;
  &:not(:last-child)::after {
    padding-left: ${spacing.half};
    content: '•';
  }

  &:last-child {
    padding-right: 0;
  }
`;

export const TemplateDetailsEllipsize = styled.div`
  flex: 0 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** Used for the campaign ID information for the page subheader */
export const TemplateDetailsFull = styled.div`
  margin-left: ${spacing.quarter};
  flex: 0 0 auto;
`;

export const TemplateDetailsLink = styled.a`
  ${link2};
  font-size: ${fontSizes.s12};
  line-height: 1.15em;
  margin-left: ${spacing.quarter};
`;

export const ExperimentVariantBanner = styled(InfoBanner)`
  margin: -${spacing.layoutTopPadding} min(
      calc((100vw - ${spacing.normalPageMaxWidth}) / -2),
      -${spacing.layoutLRPadding}
    ) ${spacing.layoutTopPadding} min(calc((100vw - ${spacing.normalPageMaxWidth}) / -2), -${spacing.layoutLRPadding});
  justify-content: center;
  border-radius: 0;
  [data-test='banner-content'] {
    flex: none;
  }
`;
